import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  ArrowRight, 
  Calendar, 
  MapPin, 
  Users, 
  Camera,
  Edit,
  QrCode,
  ExternalLink,
  Settings,
  Power
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config';

/** Build the raw overlay URL (for links). Full URLs pass through; R2 keys get the serving route prefix. */
function overlayRawUrl(value: string): string {
  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) {
    return value;
  }
  return `${API_BASE_URL}/api/events/overlays/${value}`;
}

/** Fetch an overlay image and return a blob URL to avoid CORS blocking on <img> tags. */
async function fetchOverlayBlobUrl(value: string): Promise<string> {
  if (value.startsWith('data:')) return value;
  const url = overlayRawUrl(value);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch overlay: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Build the raw background image URL. Full URLs pass through; local paths get the API base prefix. */
function backgroundRawUrl(value: string): string {
  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) {
    return value;
  }
  return `${API_BASE_URL}${value}`;
}

/** Fetch a background image and return a blob URL to avoid CORS blocking on <img> tags. */
async function fetchBackgroundBlobUrl(value: string): Promise<string> {
  if (value.startsWith('data:')) return value;
  const url = backgroundRawUrl(value);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch background: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data: event, isLoading } = useQuery(
    ['event', id],
    () => axios.get(`/api/events/${id}`).then(res => res.data.data),
    { enabled: !!id }
  );

  const [maxPhotosInput, setMaxPhotosInput] = useState<number>(10);
  const [savingMaxPhotos, setSavingMaxPhotos] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (event?.settings?.maxPhotos) {
      setMaxPhotosInput(event.settings.maxPhotos);
    }
  }, [event?.settings?.maxPhotos]);

  const queryClient = useQueryClient();

  const toggleActiveMutation = useMutation(
    (isActive: boolean) => axios.put(`/api/events/${id}`, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', id]);
        toast.success('סטטוס האירוע עודכן בהצלחה');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס האירוע');
      },
    }
  );

  const toggleBackgroundFadedMutation = useMutation(
    (faded: boolean) => axios.put(`/api/events/${id}`, { settings: { backgroundImageFaded: faded } }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', id]);
        toast.success('הגדרת שקיפות הרקע עודכנה בהצלחה');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה בעדכון שקיפות הרקע');
      },
    }
  );

  const [overlayBlobUrls, setOverlayBlobUrls] = useState<{ landscape?: string; portrait?: string }>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const urls: { landscape?: string; portrait?: string } = {};
      if (event?.settings?.overlays?.landscape) {
        try { urls.landscape = await fetchOverlayBlobUrl(event.settings.overlays.landscape); } catch (e) { console.error('Failed to load landscape overlay:', e); }
      }
      if (event?.settings?.overlays?.portrait) {
        try { urls.portrait = await fetchOverlayBlobUrl(event.settings.overlays.portrait); } catch (e) { console.error('Failed to load portrait overlay:', e); }
      }
      if (!cancelled) setOverlayBlobUrls(urls);
    };
    load();
    return () => { cancelled = true; };
  }, [event?.settings?.overlays?.landscape, event?.settings?.overlays?.portrait]);

  const [overlayUploading, setOverlayUploading] = useState(false);
  const [overlayUrlInput, setOverlayUrlInput] = useState<string>('');
  const [overlayOrientation, setOverlayOrientation] = useState<'landscape' | 'portrait'>('landscape');

  const [backgroundBlobUrl, setBackgroundBlobUrl] = useState<string | null>(null);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [backgroundResetting, setBackgroundResetting] = useState(false);
  const [backgroundUrlInput, setBackgroundUrlInput] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const raw = event?.settings?.backgroundImageUrl;
      if (!raw) { if (!cancelled) setBackgroundBlobUrl(null); return; }
      try {
        const blobUrl = await fetchBackgroundBlobUrl(raw);
        if (!cancelled) setBackgroundBlobUrl(blobUrl);
      } catch (e) {
        console.error('Failed to load background image:', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [event?.settings?.backgroundImageUrl]);

  const uploadOverlay = async (file?: File) => {
    if (!id) return;
    try {
      setOverlayUploading(true);
      let payload: any = { orientation: overlayOrientation };
      if (file) {
        const reader = new FileReader();
        const dataUrl: string = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        payload.dataUrl = dataUrl;
      } else if (overlayUrlInput.trim()) {
        payload.url = overlayUrlInput.trim();
      } else {
        toast.error('בחר קובץ או הזן קישור');
        return;
      }

      const res = await axios.post(`/api/events/${id}/overlay-frame`, payload);
      const updated = res?.data?.data;
      if (updated) {
        await queryClient.invalidateQueries(['event', id]);
        toast.success(`מסגרת ${overlayOrientation === 'portrait' ? 'פורטרט' : 'לנדסקייפ'} עודכנה`);
      }
    } catch (e: any) {
      console.error('Failed to upload overlay', e);
      toast.error(e?.response?.data?.message || 'שגיאה בהעלאת מסגרת');
    } finally {
      setOverlayUploading(false);
    }
  };

  const uploadBackground = async (file?: File) => {
    if (!id) return;
    try {
      setBackgroundUploading(true);
      let payload: any = {};
      if (file) {
        const reader = new FileReader();
        const dataUrl: string = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        payload.dataUrl = dataUrl;
      } else if (backgroundUrlInput.trim()) {
        payload.url = backgroundUrlInput.trim();
      } else {
        toast.error('בחר קובץ או הזן קישור');
        return;
      }

      const res = await axios.post(`/api/events/${id}/background-image`, payload);
      const updated = res?.data?.data;
      if (updated) {
        await queryClient.invalidateQueries(['event', id]);
        toast.success('תמונת הרקע עודכנה');
        setBackgroundUrlInput('');
      }
    } catch (e: any) {
      console.error('Failed to upload background image', e);
      toast.error(e?.response?.data?.message || 'שגיאה בהעלאת תמונת הרקע');
    } finally {
      setBackgroundUploading(false);
    }
  };

  const resetBackground = async () => {
    if (!id) return;
    try {
      setBackgroundResetting(true);
      await axios.delete(`/api/events/${id}/background-image`);
      await queryClient.invalidateQueries(['event', id]);
      toast.success('תמונת הרקע אופסה לברירת המחדל');
    } catch (e: any) {
      console.error('Failed to reset background image', e);
      toast.error(e?.response?.data?.message || 'שגיאה באיפוס תמונת הרקע');
    } finally {
      setBackgroundResetting(false);
    }
  };

  const saveMaxPhotos = async () => {
    if (!id) return;
    try {
      setSavingMaxPhotos(true);
      setSaveMessage(null);
      const res = await axios.put(`/api/events/${id}`, {
        settings: { maxPhotos: maxPhotosInput }
      });
      // Optimistically update cache with response payload if available
      const updated = res?.data?.data;
      if (updated) {
        queryClient.setQueryData(['event', id], updated);
      }
      // Also trigger a refetch to be safe
      await queryClient.invalidateQueries(['event', id]);
      // Ensure local state reflects saved value
      setMaxPhotosInput(prev => prev);
      setSaveMessage('הגדרה נשמרה בהצלחה');
    } catch (e) {
      console.error('Failed to save maxPhotos', e);
      setSaveMessage('שמירת מקסימום תמונות נכשלה');
    } finally {
      setSavingMaxPhotos(false);
    }
  };

  const { data: qrData } = useQuery(
    ['qr', id],
    () => axios.get(`/api/qr/event/${id}`).then(res => res.data.data),
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="h-32 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900">אירוע לא נמצא</h2>
          <p className="mt-2 text-gray-600">האירוע שחיפשת אינו קיים או נמחק</p>
          <Link
            to="/events"
            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500"
          >
            <ArrowRight className="h-4 w-4 ml-2" />
            חזור לרשימת האירועים
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/events"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowRight className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{event.name}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                event.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {event.isActive ? 'פעיל' : 'לא פעיל'}
              </span>
              <button
                onClick={() => toggleActiveMutation.mutate(!event.isActive)}
                disabled={toggleActiveMutation.isLoading}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  event.isActive
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                } ${toggleActiveMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={event.isActive ? 'השבת אירוע' : 'הפעל אירוע'}
              >
                <Power className="h-3.5 w-3.5" />
                {toggleActiveMutation.isLoading ? 'מעדכן...' : event.isActive ? 'השבת' : 'הפעל'}
              </button>
            </div>
            <p className="text-gray-600">{event.coupleNames.bride} & {event.coupleNames.groom}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/events/${event._id}/texts`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 ml-2" />
              ערוך טקסטים
            </Link>
            {event.siteUrl && (
              <a
                href={event.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4 ml-2" />
                צפה באתר
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">פרטי האירוע</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">תאריך האירוע</p>
                      <p className="text-sm text-gray-600">
                        {format(new Date(event.eventDate), 'dd/MM/yyyy')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">בני הזוג</p>
                      <p className="text-sm text-gray-600">
                        {event.coupleNames.bride} & {event.coupleNames.groom}
                      </p>
                    </div>
                  </div>

                  {event.venue?.name && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">מקום האירוע</p>
                        <p className="text-sm text-gray-600">{event.venue.name}</p>
                        {event.venue.address && (
                          <p className="text-sm text-gray-500">{event.venue.address}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {event.description && (
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">תיאור</p>
                      <p className="text-sm text-gray-600">{event.description}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-2">קישור לאתר</p>
                    <a
                      href={event.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-500 break-all"
                    >
                      {event.siteUrl}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">סטטיסטיקות</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Camera className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-900">{event.stats?.totalPhotos || 0}</p>
                  <p className="text-sm text-blue-600">תמונות</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-900">{event.stats?.totalUsers || 0}</p>
                  <p className="text-sm text-green-600">משתמשים</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Calendar className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-900">
                    {Math.ceil((new Date(event.eventDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}
                  </p>
                  <p className="text-sm text-purple-600">ימים לאירוע</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <Settings className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-orange-900">
                    {event.textConfig?.language || 'עברית'}
                  </p>
                  <p className="text-sm text-orange-600">שפה</p>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">הגדרות</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">הורדת תמונות</p>
                    <p className="text-sm text-gray-600">אפשר למשתמשים להוריד תמונות</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    event.settings?.allowDownload 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {event.settings?.allowDownload ? 'מופעל' : 'מבוטל'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">מדבקות</p>
                    <p className="text-sm text-gray-600">אפשר הוספת מדבקות לתמונות</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    event.settings?.enableStickers 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {event.settings?.enableStickers ? 'מופעל' : 'מבוטל'}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">מקסימום תמונות</p>
                    <p className="text-sm text-gray-600">מספר התמונות המקסימלי למשתמש</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={maxPhotosInput}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (!Number.isNaN(val)) setMaxPhotosInput(val);
                      }}
                      className="w-24 border border-gray-300 rounded-md px-2 py-1 text-sm"
                    />
                    <button
                      onClick={saveMaxPhotos}
                      disabled={savingMaxPhotos}
                      className={`px-3 py-1.5 rounded-md text-white text-sm ${savingMaxPhotos ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      {savingMaxPhotos ? 'שומר...' : 'שמור'}
                    </button>
                  </div>
                </div>
                {saveMessage && (
                  <div className="text-sm mt-2 text-gray-600">{saveMessage}</div>
                )}

                {/* Overlay frame */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-900 mb-3">מסגרת עליונה (Overlay)</p>

                  {/* Display current overlays */}
                  <div className="mb-4 space-y-2">
                    {event.settings?.overlays?.landscape ? (
                      <div className="flex items-center gap-4 p-2 bg-blue-50 rounded">
                        <span className="text-xs font-medium text-blue-700 w-20">Landscape:</span>
                        {overlayBlobUrls.landscape && <img src={overlayBlobUrls.landscape} alt="Landscape Overlay" className="h-12 w-20 object-contain border rounded" />}
                        <a href={overlayRawUrl(event.settings.overlays.landscape)} target="_blank" rel="noreferrer" className="text-blue-600 text-xs break-all flex-1">{event.settings.overlays.landscape}</a>
                      </div>
                    ) : null}
                    {event.settings?.overlays?.portrait ? (
                      <div className="flex items-center gap-4 p-2 bg-purple-50 rounded">
                        <span className="text-xs font-medium text-purple-700 w-20">Portrait:</span>
                        {overlayBlobUrls.portrait && <img src={overlayBlobUrls.portrait} alt="Portrait Overlay" className="h-12 w-20 object-contain border rounded" />}
                        <a href={overlayRawUrl(event.settings.overlays.portrait)} target="_blank" rel="noreferrer" className="text-blue-600 text-xs break-all flex-1">{event.settings.overlays.portrait}</a>
                      </div>
                    ) : null}
                    {!event.settings?.overlays?.landscape && !event.settings?.overlays?.portrait && (
                      <p className="text-sm text-gray-600">לא הוגדרו מסגרות</p>
                    )}
                  </div>

                  {/* Orientation selector */}
                  <div className="mb-3">
                    <label className="text-xs font-medium text-gray-700 mb-1 block">כיוון (Orientation):</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setOverlayOrientation('landscape')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          overlayOrientation === 'landscape'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Landscape (לרוחב)
                      </button>
                      <button
                        onClick={() => setOverlayOrientation('portrait')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          overlayOrientation === 'portrait'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Portrait (לאורך)
                      </button>
                    </div>
                  </div>

                  {/* Upload controls */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadOverlay(f);
                      }}
                      className="text-sm"
                    />
                    <div className="flex gap-2 items-center">
                      <input
                        type="url"
                        value={overlayUrlInput}
                        onChange={(e) => setOverlayUrlInput(e.target.value)}
                        placeholder="או הדבק קישור למסגרת"
                        className="w-64 border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                      <button
                        onClick={() => uploadOverlay()}
                        disabled={overlayUploading}
                        className={`px-3 py-1.5 rounded-md text-white text-sm ${overlayUploading ? 'bg-green-300' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        {overlayUploading ? 'מעדכן...' : 'עדכן קישור'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Background Image */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-900 mb-3">תמונת רקע (Background)</p>

                  {/* Opacity (faded) toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">הצג רקע עם שקיפות</p>
                      <p className="text-sm text-gray-600">כשמופעל הרקע מוצג מעומעם; כשמבוטל הרקע מוצג באטימות מלאה</p>
                    </div>
                    {(() => {
                      const faded = event.settings?.backgroundImageFaded ?? true;
                      return (
                        <button
                          onClick={() => toggleBackgroundFadedMutation.mutate(!faded)}
                          disabled={toggleBackgroundFadedMutation.isLoading}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            faded ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          } ${toggleBackgroundFadedMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {toggleBackgroundFadedMutation.isLoading ? 'מעדכן...' : faded ? 'מופעל' : 'מבוטל'}
                        </button>
                      );
                    })()}
                  </div>

                  {/* Display current background */}
                  <div className="mb-4">
                    {event.settings?.backgroundImageUrl ? (
                      <div className="flex items-center gap-4 p-2 bg-gray-50 rounded">
                        {backgroundBlobUrl && (
                          <img
                            src={backgroundBlobUrl}
                            alt="Background"
                            className="h-14 w-24 object-cover border rounded"
                          />
                        )}
                        <a
                          href={backgroundRawUrl(event.settings.backgroundImageUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 text-xs break-all flex-1"
                        >
                          {event.settings.backgroundImageUrl}
                        </a>
                        <button
                          onClick={resetBackground}
                          disabled={backgroundResetting}
                          className={`px-3 py-1.5 rounded-md text-white text-sm whitespace-nowrap ${backgroundResetting ? 'bg-red-300' : 'bg-red-500 hover:bg-red-600'}`}
                        >
                          {backgroundResetting ? 'מאפס...' : 'אפס לברירת מחדל'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">לא הוגדרה תמונת רקע — מוצגת תמונת ברירת המחדל</p>
                    )}
                  </div>

                  {/* Upload controls */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadBackground(f);
                      }}
                      className="text-sm"
                    />
                    <div className="flex gap-2 items-center">
                      <input
                        type="url"
                        value={backgroundUrlInput}
                        onChange={(e) => setBackgroundUrlInput(e.target.value)}
                        placeholder="או הדבק קישור לתמונת רקע"
                        className="w-64 border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                      <button
                        onClick={() => uploadBackground()}
                        disabled={backgroundUploading}
                        className={`px-3 py-1.5 rounded-md text-white text-sm ${backgroundUploading ? 'bg-green-300' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        {backgroundUploading ? 'מעדכן...' : 'עדכן קישור'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* QR Code */}
            {qrData && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  QR Code
                </h3>
                <div className="text-center">
                  <img 
                    src={qrData.qrCode} 
                    alt="QR Code" 
                    className="mx-auto border rounded-lg mb-4"
                  />
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.download = `qr-${event.name}.png`;
                      link.href = qrData.qrCode;
                      link.click();
                    }}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    הורד QR Code
                  </button>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">פעולות מהירות</h3>
              <div className="space-y-3">
                <Link
                  to={`/events/${event._id}/texts`}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Edit className="h-4 w-4 ml-2" />
                  ערוך טקסטים
                </Link>
                
                {event.siteUrl && (
                  <a
                    href={event.siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 ml-2" />
                    צפה באתר
                  </a>
                )}
              </div>
            </div>

            {/* Event Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">ציר זמן</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">אירוע נוצר</p>
                    <p className="text-sm text-gray-600">
                      {format(new Date(event.createdAt), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                </div>
                
                {event.stats?.lastActivity && (
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">פעילות אחרונה</p>
                      <p className="text-sm text-gray-600">
                        {format(new Date(event.stats.lastActivity), 'dd/MM/yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EventDetails;
